import { getAccessToken, logout } from './auth';

export interface ErrorPattern {
  id:          number;
  description: string;
}

export interface DeviceSearchResult {
  id:          number;
  description: string;
  ek_price:    number | null;
  artikel_nr:  string | null;
}

export interface RepairRecipeItem {
  id:                 number;
  device_id:          number;
  device_description: string;
  ek_price:           number | null;
  artikel_nr:         string | null;
}

export interface RepairRecipeGroup {
  id:       number;
  position: number;
  label:    string | null;
  items:    RepairRecipeItem[];
}

export interface RecipeServicepartner {
  id:          number;
  description: string;
}

export interface RepairRecipe {
  id:                number;
  /** true = gilt für jeden Masterartikel, nicht nur für den geöffneten. */
  is_global:         boolean;
  servicepartner:    RecipeServicepartner;
  error_pattern:     ErrorPattern;
  surcharge_net_eur: number;
  note:              string | null;
  groups:            RepairRecipeGroup[];
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(
      body.reason ?? `Server-Fehler ${resp.status}`,
      resp.status,
      body.result,
      body.fields,
    );
  }

  return body;
}

const API = `${window.location.origin}/api`;
const masterUrl = (id: number) => `${API}/masterarticles/${id}`;

export async function listErrorPatterns(): Promise<ErrorPattern[]> {
  const body = await request<ErrorPattern>(`${API}/error-patterns`, { method: 'GET' });
  return body.items ?? [];
}

export async function searchDevices(
  q: string,
  limit = 20,
  typeId?: number,
): Promise<DeviceSearchResult[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  if (typeId !== undefined) params.set('type_id', String(typeId));
  const body = await request<DeviceSearchResult>(`${API}/devices/search?${params}`, { method: 'GET' });
  return body.items ?? [];
}

export async function listRecipes(masterId: number): Promise<RepairRecipe[]> {
  const body = await request<RepairRecipe>(`${masterUrl(masterId)}/recipes`, { method: 'GET' });
  return body.items ?? [];
}

export async function createRecipe(
  masterId: number,
  payload: {
    servicepartner_location_id: number;
    error_pattern_id:           number;
    surcharge_net_eur?:         number;
    /** true = ohne Master anlegen, gilt dann für jeden Masterartikel. */
    is_global?:                 boolean;
  },
): Promise<RepairRecipe> {
  const body = await request<RepairRecipe>(`${masterUrl(masterId)}/recipes`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  return requireItem(body);
}

export async function patchRecipe(
  masterId: number,
  recipeId: number,
  payload: { surcharge_net_eur?: number; note?: string | null },
): Promise<RepairRecipe> {
  const body = await request<RepairRecipe>(`${masterUrl(masterId)}/recipes/${recipeId}`, {
    method: 'PATCH',
    body:   JSON.stringify(payload),
  });
  return requireItem(body);
}

export async function deleteRecipe(masterId: number, recipeId: number): Promise<void> {
  await request<unknown>(`${masterUrl(masterId)}/recipes/${recipeId}`, { method: 'DELETE' });
}

export async function createGroup(
  masterId: number,
  recipeId: number,
  payload: { label?: string | null } = {},
): Promise<RepairRecipeGroup> {
  const body = await request<RepairRecipeGroup>(
    `${masterUrl(masterId)}/recipes/${recipeId}/groups`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return requireItem(body);
}

export async function deleteGroup(
  masterId: number,
  recipeId: number,
  groupId: number,
): Promise<void> {
  await request<unknown>(
    `${masterUrl(masterId)}/recipes/${recipeId}/groups/${groupId}`,
    { method: 'DELETE' },
  );
}

export async function createItem(
  masterId: number,
  recipeId: number,
  groupId: number,
  payload: { device_id: number },
): Promise<RepairRecipeItem> {
  const body = await request<RepairRecipeItem>(
    `${masterUrl(masterId)}/recipes/${recipeId}/groups/${groupId}/items`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  return requireItem(body);
}

export async function deleteItem(
  masterId: number,
  recipeId: number,
  groupId: number,
  itemId: number,
): Promise<void> {
  await request<unknown>(
    `${masterUrl(masterId)}/recipes/${recipeId}/groups/${groupId}/items/${itemId}`,
    { method: 'DELETE' },
  );
}

function requireItem<T>(body: ApiEnvelope<T>): T {
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}
